import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import UpdateLayout from '../../layouts/UpdateLayout';
import AppScriptAPI from '../../services/appscriptAPI';
import { useUpdatePermissions } from '../../hooks/useUpdatePermissions';
import { useConsentModal } from '../../hooks/useConsentModal';
import AppToast from '../../components/AppToast';
import SelectWithOther from '../../components/SelectWithOther';

function UpdatePersonal() {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();
  const sheetId = searchParams.get('sheetId');
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });
  const { canEdit } = useUpdatePermissions();
  const { confirmWithConsent, ConsentModalJSX } = useConsentModal();

  const [recordData, setRecordData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [legendData, setLegendData] = useState(null);

  const [formData, setFormData] = useState({
    uniqueId: '',
    name: '',
    dateOfBirth: '',
    gender: '',
    educationLevel: '',
    otherEducationLevel: '',
    occupation: '',
    religion: '',
    otherReligion: '',
    race: '',
    otherRace: '',
    civilStatus: '',
    relationshipHoH: '',
    otherRelationshipHoH: '',
    identityCertificates: '',
    documentNumber: '',
    pensionBenefit: '',
    otherPensionBenefit: '',
  });

  useEffect(() => {
    if (!recordId || !sheetId) {
      setAlert({
        show: true,
        message:
          'Missing required parameters. Please search for the record again.',
        type: 'danger',
      });
      setLoading(false);
      return;
    }
    loadRecordData();
  }, [recordId, sheetId]);

  const loadRecordData = async () => {
    try {
      const response = await AppScriptAPI.getPersonalDetails(recordId, sheetId);

      if (!response.success) {
        setAlert({
          show: true,
          message: response.message || 'Failed to load personal details.',
          type: 'danger',
        });
        setLoading(false);
        return;
      }

      setRecordData(response.record);
      setLegendData(response.legend);

      const resolveOther = (val, options) =>
        val && !options.includes(val) ? 'Other' : val;
      const resolveOtherText = (val, options) =>
        val && !options.includes(val) ? val : '';

      const educationLevels = response.legend?.educationLevels || [];
      const religions = response.legend?.religions || [];
      const races = response.legend?.races || [];
      const relationships = response.legend?.relationships || [];
      const pensionBenefits = response.legend?.pensionBenefits || [];

      const rawEducation = response.record.educationLevel || '';
      const rawReligion = response.record.religion || '';
      const rawRace = response.record.race || '';
      const rawRelationship = response.record.relationshipHoH || '';
      const rawPension = response.record.pensionBenefit || '';

      const data = {
        uniqueId: response.record.uniqueId || '',
        name: response.record.name || '',
        dateOfBirth: response.record.dateOfBirth || '',
        gender: response.record.gender || '',
        educationLevel: resolveOther(rawEducation, educationLevels),
        otherEducationLevel: resolveOtherText(rawEducation, educationLevels),
        occupation: response.record.occupation || '',
        religion: resolveOther(rawReligion, religions),
        otherReligion: resolveOtherText(rawReligion, religions),
        race: resolveOther(rawRace, races),
        otherRace: resolveOtherText(rawRace, races),
        civilStatus: response.record.civilStatus || '',
        relationshipHoH: resolveOther(rawRelationship, relationships),
        otherRelationshipHoH: resolveOtherText(rawRelationship, relationships),
        identityCertificates: response.record.identityCertificates || '',
        documentNumber: response.record.documentNumber || '',
        pensionBenefit: resolveOther(rawPension, pensionBenefits),
        otherPensionBenefit: resolveOtherText(rawPension, pensionBenefits),
      };

      setFormData(data);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setLoading(false);
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updates = { [name]: value };

    if (value !== 'Other') {
      const clearMap = {
        educationLevel: 'otherEducationLevel',
        religion: 'otherReligion',
        race: 'otherRace',
        relationshipHoH: 'otherRelationshipHoH',
        pensionBenefit: 'otherPensionBenefit',
      };
      if (clearMap[name]) updates[clearMap[name]] = '';
    }

    setFormData({ ...formData, ...updates });
  };

  const hasChanges = () => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  };

  const validateDate = (dateString) => {
    if (!dateString) return { valid: true };

    const date = new Date(dateString);
    const today = new Date();

    if (isNaN(date.getTime())) {
      return { valid: false, message: 'Invalid date format.' };
    }

    if (date > today) {
      return {
        valid: false,
        message: 'Date of birth cannot be in the future.',
      };
    }

    const age = Math.floor((today - date) / (365.25 * 24 * 60 * 60 * 1000));
    if (age > 150) {
      return { valid: false, message: 'Date of birth seems unrealistic.' };
    }

    return { valid: true };
  };

  const handleSave = async () => {
    // Validate
    const dateValidation = validateDate(formData.dateOfBirth);
    if (!dateValidation.valid) {
      setAlert({ show: true, message: dateValidation.message, type: 'danger' });
      return;
    }

    if (!hasChanges()) {
      setAlert({
        show: true,
        message: 'No changes detected.',
        type: 'warning',
      });
      return;
    }

    if (!(await confirmWithConsent())) {
      return;
    }

    setSaving(true);

    try {
      const resolveField = (field, otherField) =>
        formData[field] === 'Other' && formData[otherField]
          ? formData[otherField]
          : formData[field];

      const updateData = {
        uniqueId: recordData.uniqueId,
        dateOfBirth: formData.dateOfBirth,
        gender: formData.gender,
        educationLevel: resolveField('educationLevel', 'otherEducationLevel'),
        occupation: formData.occupation.trim(),
        religion: resolveField('religion', 'otherReligion'),
        race: resolveField('race', 'otherRace'),
        civilStatus: formData.civilStatus,
        relationshipHoH: resolveField('relationshipHoH', 'otherRelationshipHoH'),
        identityCertificates: formData.identityCertificates,
        documentNumber: formData.documentNumber.trim(),
        pensionBenefit: resolveField('pensionBenefit', 'otherPensionBenefit'),
        sheetId: recordData.sheetId,
        rowIndex: recordData.rowIndex,
      };

      const response = await AppScriptAPI.updatePersonalDetails(updateData);

      if (response.success) {
        setAlert({
          show: true,
          message: 'Personal details updated successfully.',
          type: 'success',
        });
        setOriginalData(JSON.parse(JSON.stringify(formData)));
      } else {
        setAlert({
          show: true,
          message:
            'Failed to update: ' + (response.message || 'Unknown error'),
          type: 'danger',
        });
      }
    } catch (error) {
      setAlert({
        show: true,
        message: 'An error occurred: ' + error.message,
        type: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/update/housing/${recordId}?sheetId=${sheetId}`);
  };

  const handlePrevious = () => {
    navigate(`/update/contact/${recordId}?sheetId=${sheetId}`);
  };

  if (loading) {
    return (
      <UpdateLayout
        currentStep={3}
        title="Update Personal Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Loading personal details...</p>
        </div>
      </UpdateLayout>
    );
  }

  if (!recordData) {
    return (
      <UpdateLayout
        currentStep={3}
        title="Update Personal Details"
        recordId={recordId}
        sheetId={sheetId}
      >
        <Alert variant="danger">
          <strong>Error!</strong>
          <br />
          {alert.message || 'Failed to load personal details.'}
        </Alert>
      </UpdateLayout>
    );
  }

  return (
    <UpdateLayout
      currentStep={3}
      title="Update Personal Details"
      recordId={recordId}
      sheetId={sheetId}
      onSave={handleSave}
      onNext={handleNext}
      onPrevious={handlePrevious}
      isSaving={saving}
      hasChanges={hasChanges()}
      canEdit={canEdit}
    >
      {ConsentModalJSX}
      <AppToast
        show={alert.show}
        type={alert.type}
        message={alert.message}
        onClose={() => setAlert(a => ({ ...a, show: false }))}
      />

      <Form>
        <div className="section-header">Record Information (Read Only)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Unique ID</Form.Label>
              <Form.Control
                type="text"
                value={formData.uniqueId}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                className="readonly-field"
                readOnly
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="section-header">Personal Details (Editable)</div>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date of Birth</Form.Label>
              <Form.Control
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                disabled={!canEdit}
              />
              <Form.Text>Select date of birth.</Form.Text>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Gender</Form.Label>
              <div>
                <Form.Check
                  inline
                  type="radio"
                  label="Male"
                  name="gender"
                  value="Male"
                  checked={formData.gender === 'Male'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Female"
                  name="gender"
                  value="Female"
                  checked={formData.gender === 'Female'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
                <Form.Check
                  inline
                  type="radio"
                  label="Other"
                  name="gender"
                  value="Other"
                  checked={formData.gender === 'Other'}
                  onChange={handleInputChange}
                  disabled={!canEdit}
                />
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Education Level</Form.Label>
              <SelectWithOther
                name="educationLevel"
                value={formData.educationLevel}
                otherName="otherEducationLevel"
                otherValue={formData.otherEducationLevel}
                options={legendData?.educationLevels || []}
                onChange={handleInputChange}
                disabled={!canEdit}
                otherPlaceholder="Please specify education level"
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Occupation</Form.Label>
              <Form.Control
                type="text"
                name="occupation"
                value={formData.occupation}
                onChange={handleInputChange}
                placeholder="Enter occupation"
                maxLength="100"
                disabled={!canEdit}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Religion</Form.Label>
              <SelectWithOther
                name="religion"
                value={formData.religion}
                otherName="otherReligion"
                otherValue={formData.otherReligion}
                options={legendData?.religions || []}
                onChange={handleInputChange}
                disabled={!canEdit}
                otherPlaceholder="Please specify religion"
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Nationality</Form.Label>
              <SelectWithOther
                name="race"
                value={formData.race}
                otherName="otherRace"
                otherValue={formData.otherRace}
                options={legendData?.races || []}
                onChange={handleInputChange}
                disabled={!canEdit}
                otherPlaceholder="Please specify nationality"
              />
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Civil Status</Form.Label>
              <Form.Select
                name="civilStatus"
                value={formData.civilStatus}
                onChange={handleInputChange}
                disabled={!canEdit}
              >
                <option value="">Select...</option>
                {legendData?.civilStatuses?.map((item, index) => (
                  <option key={index} value={item}>
                    {item}
                  </option>
                )) ?? (
                  <>
                    <option value="Married">Married</option>
                    <option value="Unmarried">Unmarried</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Separated">Separated</option>
                    <option value="Widowed">Widowed</option>
                  </>
                )}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Relationship with Head of Household</Form.Label>
              <SelectWithOther
                name="relationshipHoH"
                value={formData.relationshipHoH}
                otherName="otherRelationshipHoH"
                otherValue={formData.otherRelationshipHoH}
                options={legendData?.relationships || []}
                onChange={handleInputChange}
                disabled={!canEdit}
                otherPlaceholder="Please specify relationship"
              />
            </Form.Group>
          </Col>
        </Row>

        <Form.Group className="mb-3">
          <Form.Label>Are there any certificates of identity?</Form.Label>
          <Form.Select
            name="identityCertificates"
            value={formData.identityCertificates}
            onChange={handleInputChange}
            disabled={!canEdit}
          >
            <option value="">Select...</option>
            {legendData?.identityCertificates?.map((item, index) => (
              <option key={index} value={item}>
                {item}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Document or ID Number</Form.Label>
          <Form.Control
            type="text"
            name="documentNumber"
            value={formData.documentNumber}
            onChange={handleInputChange}
            placeholder="Enter document/ID number or N/A"
            maxLength="50"
            disabled={!canEdit}
          />
          <Form.Text>
            Enter the number of any document or ID listed above. If you do not
            have any, enter N/A.
          </Form.Text>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Is there a pension benefit?</Form.Label>
          <SelectWithOther
            name="pensionBenefit"
            value={formData.pensionBenefit}
            otherName="otherPensionBenefit"
            otherValue={formData.otherPensionBenefit}
            options={legendData?.pensionBenefits || []}
            onChange={handleInputChange}
            disabled={!canEdit}
            otherPlaceholder="Please specify pension benefit"
          />
        </Form.Group>
      </Form>
    </UpdateLayout>
  );
}

export default UpdatePersonal;
